'use client';

import { filterXSS } from 'xss';
import { useMeasure, useToggle } from 'react-use';
import { animated, useSpring } from 'react-spring';
import styled, { css } from 'styled-components';
import { IconArrowUp } from '@/app/common/icons/libraryIcons';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { CallBackLink } from '@/app/components/hotel-detail/CallBackLink';
import { trackShowMoreDescription } from '@/app/components/hotel-detail/trackInteractionEvents';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { URLParameters } from '@/app/common/urlParameters';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { DsaInfo, hasDsaContent } from './DsaInfo';
import { type HotelStaticData } from './common/staticHotelDataAdapter';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';
import { useIsMobile } from './hooks/useMediaSSR';

type HotelPageOverviewSectionProps = {
  showCollapsed?: boolean;
  withCard?: boolean;
} & React.HTMLAttributes<HTMLElement>;

type SectionDescriptionProps = {
  descriptions: [string, string[]][];
  isMobile: boolean | undefined;
  showReadMore?: boolean;
  isEvo?: boolean;
} & React.HTMLAttributes<HTMLElement>;

const descriptionTitleKeys: { [key: string]: string } = {
  GENERAL: 'vacation.select.description.type.general',
  LOCATION: 'vacation.select.description.type.location',
  EXTERIOR: 'vacation.select.description.type.exterior',
  RESTAURANT: 'vacation.select.description.type.restaurant',
  FACILITIES: 'vacation.select.description.type.facilities',
  SPORT: 'vacation.select.description.type.sports',
  ROOMS: 'vacation.select.description.type.rooms',
  CANCELLATION_POLICY: 'vacation.select.description.type.cancellation',
  PAYMENT_INFO: 'vacation.select.description.type.payment',
  OTHER_INFOS: 'vacation.select.description.type.other',
};

const StyledDiv = styled.div``;
const AnimatedDiv = animated(StyledDiv);

// The DSA trader disclosure is a legal notice and rides inside this section, so the section has to
// survive a hotel that carries one but no editorial text — travel-api fills `dsa` and
// `descriptionMap` from unrelated sources. It has to ask DsaInfo whether that object actually
// prints anything, because a hollow one would otherwise buy a heading and a stray rule. Callers
// that wrap the section in their own chrome (a divider, a modal tab) share this predicate so the
// chrome disappears with it.
export function hasOverviewContent(staticData: HotelStaticData | null | undefined) {
  return Boolean(staticData?.description?.length) || hasDsaContent(staticData?.dsaInfo);
}

export default function HotelPageOverviewSection(props: HotelPageOverviewSectionProps) {
  const { showCollapsed = true, withCard = true, ...rest } = props;
  const { t } = useTranslation();
  const { commonData } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: staticData } = useFetchHotelStatic();

  return hasOverviewContent(staticData) ? (
    <HotelPageContentSection
      {...rest}
      title={t('vacation.select.overview.title')}
      css={css`
        display: flex;
        flex-direction: column;
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          background-color: inherit;
        }
      `}
    >
      <SectionDescription
        showReadMore={showCollapsed}
        descriptions={staticData?.description ?? []}
        isMobile={isMobile}
        // The evo/modal presentation (no card) also uses the larger Body font scale to
        // match the room card's "see room info" link.
        isEvo={!withCard}
        className="hotel-description-container"
        css={
          withCard
            ? `
          @media ${customCss.breakpoints.sm} {
            background-color: white;
            padding: 24px;
            border-radius: 8px;
          }
        `
            : undefined
        }
      />
    </HotelPageContentSection>
  ) : null;
}

function SectionDescription(props: SectionDescriptionProps) {
  const { descriptions, showReadMore, isMobile, isEvo = false, ...rest } = props;
  const MAX_HEIGHT = 180;
  const [isOpen, toggle] = useToggle(false);
  const [ref, { height }] = useMeasure();

  const shouldShowReadMore = showReadMore && height > 300;
  const style = useSpring({
    from: { maxHeight: shouldShowReadMore ? MAX_HEIGHT : height || '100%' },
    to: { maxHeight: isOpen || !shouldShowReadMore ? height || '100%' : MAX_HEIGHT },
  });

  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const { data: staticData } = useFetchHotelStatic();
  const { starTrack } = useCommonClient();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;

  const labelShowMore = t('vacation.select.details.description.readmore');
  const labelShowLess = t('vacation.select.details.description.readless');

  const onToggleReadMore = () => {
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'HOTEL_INFO',
      })
    );
    sendCuriosityClickEvent({
      event_name: 'click_hotel_description_read_more',
      event_value: isOpen ? 'to_collapse_it' : 'to_expand_it',
    });
    trackShowMoreDescription(isOpen);
    toggle();
  };

  const descriptionSections = descriptions.map((description) => (
    <Section key={description[0]}>
      <Text variant="Title5" forceTag="h3">
        {t(descriptionTitleKeys[description[0]])}
      </Text>
      {description[1].map((text, index) =>
        text ? (
          <Text
            variant={isEvo ? 'Body' : 'BodySmall'}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            forceTag="div"
            dangerouslySetInnerHTML={{ __html: filterXSS(text) }}
            css={css`
              color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
            `}
          />
        ) : null
      )}
    </Section>
  ));

  return (
    <div {...rest}>
      <div
        css={`
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        <AnimatedDiv
          id="showmore-content"
          style={{
            overflow: 'hidden',
            ...style,
          }}
        >
          {/* Without "Read more" the text is never measured: height stays 0, so maxHeight stays
              '100%', which is no limit against this wrapper's auto height. That keeps the full text
              from lagging behind a measured clamp when it reflows taller, while overflow: hidden
              still contains wide description markup. */}
          <DescriptionContainer
            ref={showReadMore ? (ref as React.Ref<HTMLDivElement>) : undefined}
            css={css`
              color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
              font-size: ${(p) => p.theme.kallisto.font.size['175']};
            `}
          >
            {descriptionSections}
            {staticData?.dsaInfo && <DsaInfo dsaInfo={staticData?.dsaInfo} countryId={staticData?.countryId} />}
          </DescriptionContainer>
        </AnimatedDiv>
      </div>
      {shouldShowReadMore ? (
        isMobile ? (
          <div
            id="showmore-button"
            css={css`
              display: inline-block;
              cursor: pointer;
              padding-top: 8px;
            `}
          >
            <ReadMore $isEvo={isEvo} onClick={onToggleReadMore}>
              {isOpen ? labelShowLess : labelShowMore}
            </ReadMore>
          </div>
        ) : (
          <div
            id="showmore-button"
            css={css`
              display: inline-block;
              cursor: pointer;
              margin-left: -8px;
              margin-top: 24px;
              padding: 0 8px;
            `}
          >
            <CallBackLink
              role="button"
              variant="secondary"
              appearance="underlined"
              onClick={onToggleReadMore}
              css={css`
                display: flex;
                align-items: center;
                ${
                  isEvo
                    ? ({ theme }) => css`
                        color: ${theme.kallisto.color.text.static.textPrimary};
                        ${theme.kallisto.mixins.font('Body')};
                      `
                    : ({ theme }) => theme.kallisto.mixins.font('BodySmall')
                };
              `}
            >
              {isOpen ? labelShowLess : labelShowMore}
              <IconArrowUp
                css={css`
                  height: 1.25rem;
                  width: 1.25rem;
                  transform: rotate(${isOpen ? '0deg' : '180deg'});
                  transition: transform 0.2s;
                  margin-left: 8px;
                  color: ${
                    isEvo
                      ? (p) => p.theme.kallisto.color.text.static.textPrimary
                      : (p) => p.theme.kallisto.color.icon.interactive.iconPrimaryDefault
                  };

                  @media ${customCss.breakpoints.sm} {
                    height: 1rem;
                    width: 1rem;
                  }
                `}
              />
            </CallBackLink>
          </div>
        )
      ) : undefined}
    </div>
  );
}

const ReadMore = styled.div<{ $isEvo?: boolean }>`
  color: ${({ theme, $isEvo }) =>
    $isEvo ? theme.kallisto.color.text.static.textPrimary : theme.kallisto.color.text.interactive.textPrimaryDefault};
  cursor: pointer;
  text-decoration: ${({ $isEvo }) => ($isEvo ? 'underline' : 'none')};
  ${({ theme, $isEvo }) => theme.kallisto.mixins.font($isEvo ? 'Body' : 'BodySmall')};
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
